import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import { installMoriaUi } from './bootstrap/moriaUi'
import { router } from './router'
import { useThemeStore } from './stores/theme'
import { useMetadataStore } from './stores/metadata'
import { setupApiInterceptors } from './api/interceptors'
import { configureClientEventContext, installClientErrorHandlers } from './observability/reporter'
import { isDevBuild } from './utils/isDev'
import './styles/globals.css'
import 'primeicons/primeicons.css'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
// Initialize the theme store before mounting so the `html.moria-dark`
// class is in sync with the Pinia state. The inline script in index.html
// has already applied the right class for first paint; this call reconciles
// it with the store.
useThemeStore()
const metadata = useMetadataStore()
window.addEventListener('focus', () => {
  void metadata.refreshIfStale()
})

async function mountApplication(): Promise<void> {
  if (isDevBuild) {
    // @ts-expect-error -- dev-server-only URL is intentionally absent from production resolution.
    const previews = await import(/* @vite-ignore */ '/src/devtools/useDevFeaturePreviews.ts')
    await previews.initializeDevFeaturePreviews()
  }

  app.use(router)

  configureClientEventContext(() => {
    const currentRoute = router.currentRoute.value
    const pageName = typeof currentRoute.name === 'string' ? currentRoute.name : undefined
    return pageName ? { page_name: pageName } : {}
  })
  installClientErrorHandlers(app)

  // Install after pinia + router so stores and navigation are available
  // inside the interceptor when it fires.
  setupApiInterceptors(router)

  installMoriaUi(app)

  app.mount('#app')
}

void mountApplication()
